import React, { useState, useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";

import HomePage from "./pages/HomePage";
import PregnancyTracker from "./pages/PregnancyTracker";
import Community from "./pages/Community";
import Appointments from "./pages/Appointments";
import Profile from "./pages/Profile";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Shopping from "./pages/Shopping";
import MedicalRecords from "./pages/MedicalRecords";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import DataDeletion from "./pages/DataDeletion";

import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Button } from "./components/ui/button";
import { X, Facebook, Bell } from "lucide-react";

import FloatingChatbotButton from "./components/FloatingChatbotButton";
import ChatbotPopup from "./components/ChatbotPopup";

const API_URL = process.env.REACT_APP_API_URL || 'https://baby-app-backend.onrender.com';

// Facebook Connection Notification Banner
const FacebookNotificationBanner = () => {
  const { isLoggedIn, currentUser } = useAuth();
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const checkFacebookStatus = async () => {
      if (!isLoggedIn || !currentUser?.id || dismissed) {
        setShowBanner(false);
        return;
      }

      // Don't show on profile page (user is already there)
      if (location.pathname === '/profile') {
        setShowBanner(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/notification-settings/${currentUser.id}`);
        if (response.ok) {
          const data = await response.json();
          // Show banner if Facebook is not linked
          setShowBanner(!data.facebook_linked);
        }
      } catch (error) {
        console.error('Error checking Facebook status:', error);
      }
    };

    checkFacebookStatus();
  }, [isLoggedIn, currentUser?.id, dismissed, location.pathname]);

  if (!showBanner) return null;

  return (
    <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 shadow-lg">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-full">
            <Facebook className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-sm">
              🔔 Kết nối Facebook để nhận thông báo hàng ngày về sức khỏe thai kỳ!
            </p>
            <p className="text-xs text-white/80">
              Nhận lời khuyên dinh dưỡng, nhắc nhở lịch khám thai qua Messenger
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className="bg-white text-blue-600 hover:bg-blue-50"
            onClick={() => navigate('/profile')}
          >
            <Bell className="h-4 w-4 mr-1" />
            Cài đặt ngay
          </Button>
          <button
            onClick={() => {
              setShowBanner(false);
              setDismissed(true);
            }}
            className="p-1 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// App Content with Router
const AppContent = () => {
  const [chatOpen, setChatOpen] = useState(false);

  return (
    <>
      <FacebookNotificationBanner />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/pregnancy-tracker" element={<PregnancyTracker />} />
        <Route path="/community" element={<Community />} />
        <Route path="/appointments" element={<Appointments />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/shopping" element={<Shopping />} />
        <Route path="/medical-records" element={<MedicalRecords />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-service" element={<TermsOfService />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
      </Routes>

      {/* CHATBOT */}
      <FloatingChatbotButton onClick={() => setChatOpen(true)} />
      {chatOpen && <ChatbotPopup onClose={() => setChatOpen(false)} />}

      <Toaster />
    </>
  );
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;